import React, { useState, memo } from 'react';
import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import Col from 'reactstrap/lib/Col';
import { InventoryEntities } from 'client/data/models/inventory';
import { getAspectRatio } from 'site-modules/shared/utils/inventory-image-utils';
import { PreventVerticalScrollOnSwipe } from 'site-modules/shared/components/carousel/prevent-vertical-scroll-on-swipe';
import { PhotoCountBadge } from 'site-modules/shared/components/inventory/photo-count-badge/photo-count-badge';
import { InventoryThumbnailsRow } from 'site-modules/inventory/components/inventory-thumbnails/inventory-thumbnails-row';
import { SHOWN_ALL_PHOTOS_COUNT } from 'site-modules/inventory/constants/inventory-thumbnails';
import { InventoryHeroCarousel } from 'site-modules/inventory/components/inventory-hero/inventory-hero-carousel/inventory-hero-carousel';
import { InventoryHeroStaticImage } from 'site-modules/inventory/components/inventory-hero/inventory-hero-static-image/inventory-hero-static-image';
import { StockPhotoLabel } from 'site-modules/inventory/components/inventory-hero/stock-photo-label/stock-photo-label';

import './inventory-hero-image.scss';

export const InventoryHeroImage = memo(
  ({
    vinData,
    isMobile = false,
    showMultipleImages = false,
    showSliderArrows = false,
    toggleModal,
    onIndexChange,
    isStockPhotos = false,
    photoUrls = [],
    hasPhoto360 = false,
    hasPhotos = false,
    thumbnailPhotoUrls = [],
    photoType = '',
    forwardedRef = null,
  }) => {
    const [hasError, setHasError] = useState(false);
    const vin = get(vinData, 'vin');
    const { make, model, year, bodyType } = get(vinData, 'vehicleInfo.styleInfo', {});

    const heroPhotoUrl = get(photoUrls[0], 'url');
    const photoCount = photoUrls.length;
    const hasMultipleImages = showMultipleImages && photoCount > 1;

    const fallbackAspectRatio = isMobile ? '4:3' : '3:2';
    const photoContainerAspectRatio = getAspectRatio(photoUrls[0], fallbackAspectRatio);

    const alt = year && make && model ? `The ${year} ${make} ${model}` : 'The vehicle';

    const thumbnailsPhotoUrlsLength = thumbnailPhotoUrls.length;
    const thumbnailPhotoUrlsToDisplay = thumbnailPhotoUrls.slice(1);

    return (
      <>
        <section
          className="inventory-hero-image px-0 mx-md-0 pos-r d-block py-0_5 py-md-0"
          data-tracking-parent="edm-inventory-hero-image"
        >
          <Col
            className="inventory-hero-image-slide-content d-flex justify-content-between flex-column"
            style={{ position: 'absolute' }}
          >
            {hasPhotos && !hasError && <PhotoCountBadge photoCount={photoCount} />}

            {isStockPhotos && <StockPhotoLabel isMobile={isMobile} />}
          </Col>
          {hasMultipleImages && (
            <PreventVerticalScrollOnSwipe>
              <InventoryHeroCarousel
                showSliderArrows={showSliderArrows}
                photoUrls={photoUrls}
                photoContainerAspectRatio={photoContainerAspectRatio}
                make={make}
                model={model}
                year={year}
                vin={vin}
                isMobile={isMobile}
                bodyType={bodyType}
                hasPhoto360={hasPhoto360}
                onOpenPhotoflipper={toggleModal}
                onIndexChange={onIndexChange}
                ref={forwardedRef}
                photoCount={photoCount}
              />
            </PreventVerticalScrollOnSwipe>
          )}

          {!hasMultipleImages && (
            <InventoryHeroStaticImage
              heroPhotoUrl={heroPhotoUrl}
              isMobile={isMobile}
              alt={alt}
              photoContainerAspectRatio={photoContainerAspectRatio}
              hasPhotos={hasPhotos}
              bodyType={bodyType}
              vin={vin}
              hasPhoto360={hasPhoto360}
              hasError={hasError}
              setHasError={setHasError}
              onClick={toggleModal}
              photoCount={photoCount}
            />
          )}
        </section>
        {showMultipleImages && thumbnailsPhotoUrlsLength > SHOWN_ALL_PHOTOS_COUNT && (
          <InventoryThumbnailsRow
            photoUrls={thumbnailPhotoUrlsToDisplay}
            photoCount={thumbnailsPhotoUrlsLength}
            title={alt}
            onThumbnailClick={toggleModal}
            onIndexChange={onIndexChange}
            vinData={vinData}
            photoType={photoType}
          />
        )}
      </>
    );
  }
);

InventoryHeroImage.propTypes = {
  vinData: InventoryEntities.InventoryVin.isRequired,
  isMobile: PropTypes.bool,
  showMultipleImages: PropTypes.bool,
  showSliderArrows: PropTypes.bool,
  toggleModal: PropTypes.func.isRequired,
  onIndexChange: PropTypes.func.isRequired,
  isStockPhotos: PropTypes.bool,
  hasPhoto360: PropTypes.bool,
  photoUrls: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string, photoType: PropTypes.string })),
  hasPhotos: PropTypes.bool,
  thumbnailPhotoUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
  photoType: PropTypes.string,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

InventoryHeroImage.displayName = 'InventoryHeroImage';
